import { Injectable, inject } from '@angular/core';
import { Breadcrumb } from '@tc-angular/design-system';
import { catchError, filter, map, Observable, of, take } from 'rxjs';
import { BreadcrumbResolver } from './breadcrumb-resolver';
import { BOOKINGS_FACADE, GetBookingParams } from '../../services/facades';
import { BasicDataResult } from '../../services/data-orchestrator';
import { validate as uuidValidate } from 'uuid';
import { BookingsRouteSegments } from 'src/app/shared/constants';
import { BookingDto } from 'src/app/shared/dtos/booking/booking.dto';

@Injectable({ providedIn: 'root' })
export class BookingsBreadcrumbResolver implements BreadcrumbResolver {
  private readonly bookingsFacade = inject(BOOKINGS_FACADE);

  private readonly baseBreadcrumbs: Breadcrumb[] = [
    {
      label: 'nav.dashboard',
      routerLink: ['/'],
    },
    {
      label: 'nav.bookings',
      routerLink: ['/', BookingsRouteSegments.Bookings],
    },
  ];

  public resolve(params: unknown): Observable<Breadcrumb[]> {
    if (!this.validRouteParams(params)) {
      return of(this.baseBreadcrumbs);
    }

    const bookingParams = {
      id: params.bookingId,
      readFromCacheFirst: true,
    } satisfies GetBookingParams;

    // TODO: we don't cache this data so this triggers a new API request
    return this.bookingsFacade.getBooking(bookingParams).data$.pipe(
      filter((result: BasicDataResult<BookingDto>) => !result.loading),
      take(1),
      map((result: BasicDataResult<BookingDto>) =>
        result.error ? this.baseBreadcrumbs : this.mapDataToBreadcrumbs(result.data),
      ),
      catchError((err: unknown) => {
        console.error(err);
        return of(this.baseBreadcrumbs);
      }),
    );
  }

  private validRouteParams(params: unknown): params is BookingsRouteParams {
    if (!params || typeof params !== 'object') {
      return false;
    }

    const p = params as Record<string, unknown>;
    const bookingIdParam = p[BookingsRouteSegments.BookingId];

    if (typeof bookingIdParam !== 'string' || !uuidValidate(bookingIdParam)) {
      return false;
    }

    return true;
  }

  private mapDataToBreadcrumbs(booking: BookingDto): Breadcrumb[] {
    // Booking name is an optional field
    // Show booking reference in breadcrumb when it's omitted
    const bookingLabel = booking.Name ?? 'nav.ref';
    const bookingLabelTranslateParams = booking.Name ? undefined : { id: booking.PhenixBookingId };

    return [
      ...this.baseBreadcrumbs,
      {
        label: bookingLabel,
        labelTranslateParams: bookingLabelTranslateParams,
        backNavigationEnabled: true,
        routerLink: undefined,
      } satisfies Breadcrumb,
    ];
  }
}

interface BookingsRouteParams {
  readonly bookingId: string;
}
